// src/hooks/useUseCaseStatuses.ts
// Custom hook for managing use case statuses and decisions in Step 2 (Analysis & Qualification)

import { useCallback, useEffect, useMemo, useState } from 'react';

export type DecisionValue =
  | 'undecided'
  | 'proceed'
  | 'refine'
  | 'monitor'
  | 'defer'
  | 'reject';

export type StatusValue =
  | 'prospect'
  | 'qualified'
  | 'roadmapping'
  | 'approval'
  | 'execution'
  | 'rejected';

export interface UseCaseStatus {
  use_case_id: number;
  status: StatusValue;
  decision?: DecisionValue;
  decision_note?: string | null;
  updated_at: string;
  updated_by?: string;
}

interface UseCaseStatusesHook {
  statuses: UseCaseStatus[];
  statusByUseCaseId: Map<number, UseCaseStatus>;
  isLoading: boolean;
  error: Error | null;
  updateStatus: (
    useCaseId: number,
    patch: {
      status?: StatusValue;
      decision?: DecisionValue;
      decision_note?: string | null;
    }
  ) => Promise<void>;
  refetch: () => Promise<void>;
}

const API_BASE = `${import.meta.env.VITE_BACKEND_URL}/api/v2`;

export function useUseCaseStatuses(
  clientId: string | number | null
): UseCaseStatusesHook {
  const [statuses, setStatuses] = useState<UseCaseStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStatuses = useCallback(async () => {
    if (!clientId) {
      console.warn('useUseCaseStatuses: No clientId provided');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/usecases/${clientId}/statuses`);

      if (!response.ok) {
        throw new Error('Failed to load use case statuses');
      }

      const data = await response.json();

      // Handle both { success: true, statuses: [...] } and { statuses: [...] } formats
      const statusesArray = data.statuses || data.data || [];
      setStatuses(statusesArray);
    } catch (err: any) {
      console.error('Failed to fetch statuses', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const updateStatus = useCallback(
    async (
      useCaseId: number,
      patch: {
        status?: StatusValue;
        decision?: DecisionValue;
        decision_note?: string | null;
      }
    ) => {
      if (!clientId) {
        console.error('Cannot update status: no clientId');
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/usecases/${clientId}/${useCaseId}/status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `Failed to update use case status: ${response.status}`
          );
        }

        // Refresh statuses after update
        await fetchStatuses();
      } catch (err) {
        console.error('Failed to update use case status', err);
        throw err; // Re-throw so caller can handle
      }
    },
    [clientId, fetchStatuses]
  );

  const statusByUseCaseId = useMemo(
    () => new Map(statuses.map((s) => [s.use_case_id, s] as const)),
    [statuses]
  );

  return {
    statuses,
    statusByUseCaseId,
    isLoading,
    error,
    updateStatus,
    refetch: fetchStatuses,
  };
}

